import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDemandReviewerDecisionDto,
  CreateDemandReviewerDecisionResponseDto,
  CreateDemandReviewerDto,
  CreateDemandReviewerResponseDto,
  DemandReviewerDecisionDto,
  DemandReviewerDto,
  GetDemandReviewerDecisionsResponseDto,
  GetDemandReviewersResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { demandsQueryKeys } from '@/features/intake/queryKeys/demands';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useDemandReviewersApi = (httpClient: AxiosInstance) => {
  const getDemandReviewers = async (demandId: number): Promise<DemandReviewerDto[]> => {
    const { data } = await httpClient.get<GetDemandReviewersResponseDto>(
      `/demands/${demandId}/reviewers`,
    );
    return data.data;
  };

  const createDemandReviewer = async ({
    demandId,
    dto,
  }: {
    demandId: number;
    dto: CreateDemandReviewerDto;
  }): Promise<DemandReviewerDto> => {
    const { data } = await httpClient.post<CreateDemandReviewerResponseDto>(
      `/demands/${demandId}/reviewers`,
      dto,
    );
    return data.data;
  };

  const removeDemandReviewer = async ({
    demandId,
    reviewerRecordId,
  }: {
    demandId: number;
    reviewerRecordId: number;
  }): Promise<void> => {
    await httpClient.delete(`/demands/${demandId}/reviewers/${reviewerRecordId}`);
  };

  const remindDemandReviewer = async ({
    demandId,
    reviewerRecordId,
  }: {
    demandId: number;
    reviewerRecordId: number;
  }): Promise<void> => {
    await httpClient.post(`/demands/${demandId}/reviewers/${reviewerRecordId}/remind`);
  };

  const getReviewerDecisions = async (
    demandId: number,
    reviewerRecordId: number,
  ): Promise<DemandReviewerDecisionDto[]> => {
    const { data } = await httpClient.get<GetDemandReviewerDecisionsResponseDto>(
      `/demands/${demandId}/reviewers/${reviewerRecordId}/decisions`,
    );
    return data.data;
  };

  const recordReviewerDecision = async ({
    demandId,
    reviewerRecordId,
    dto,
  }: {
    demandId: number;
    reviewerRecordId: number;
    dto: CreateDemandReviewerDecisionDto;
  }): Promise<DemandReviewerDecisionDto> => {
    const { data } = await httpClient.post<CreateDemandReviewerDecisionResponseDto>(
      `/demands/${demandId}/reviewers/${reviewerRecordId}/decisions`,
      dto,
    );
    return data.data;
  };

  const queryClient = useQueryClient();
  // The `reviews` step needs at least one reviewer and a unanimous approval, so adding, removing and
  // deciding can all move it - which is why the checklist is invalidated for all three rather than
  // only for decisions.
  const invalidate = (demandId: number) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.reviewers(demandId) }),
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.activity(demandId) }),
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.checklist(demandId) }),
    ]);

  const useGetReviewers = (demandId: number) =>
    useQuery({
      queryKey: demandsQueryKeys.reviewers(demandId),
      queryFn: () => getDemandReviewers(demandId),
      enabled: demandId > 0,
    });

  const useAddReviewer = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandReviewer,
      onSuccess: (_data, variables) => invalidate(variables.demandId),
    });

  const useRemoveReviewer = () =>
    useErrorHandlingMutation({
      mutationFn: removeDemandReviewer,
      onSuccess: (_data, variables) => invalidate(variables.demandId),
    });

  // Nothing to invalidate: a reminder sends a notification and changes no demand data.
  const useRemindReviewer = () =>
    useErrorHandlingMutation({
      mutationFn: remindDemandReviewer,
    });

  const useReviewerDecisions = (demandId: number, reviewerRecordId: number) =>
    useQuery({
      queryKey: demandsQueryKeys.reviewerDecisions(demandId, reviewerRecordId),
      queryFn: () => getReviewerDecisions(demandId, reviewerRecordId),
      enabled: demandId > 0 && reviewerRecordId > 0,
    });

  const useRecordDecision = () =>
    useErrorHandlingMutation({
      mutationFn: recordReviewerDecision,
      // Both: the reviewers list carries decisionCount and latestDecision, and the row's own history
      // is a separate query that a new decision has just invalidated too.
      onSuccess: (_data, variables) => {
        invalidate(variables.demandId);
        return queryClient.invalidateQueries({
          queryKey: demandsQueryKeys.reviewerDecisions(
            variables.demandId,
            variables.reviewerRecordId,
          ),
        });
      },
    });

  return {
    getDemandReviewers,
    getReviewerDecisions,
    useGetReviewers,
    useAddReviewer,
    useRemoveReviewer,
    useRemindReviewer,
    useReviewerDecisions,
    useRecordDecision,
  };
};
